/**
 * Iter-52: shared landing-page session hook.
 *
 * Owns the two things every landing-page experience (Standard OR Quiz)
 * needs to share:
 *   1. An anonymous, sticky visitor session ID (localStorage keyed by
 *      slug, non-PII, used for A/B stickiness and funnel analytics).
 *   2. A page-mount timestamp used as `load_ms` in the final lead
 *      submission — quiz sessions get their timing measured across the
 *      ENTIRE quiz walkthrough (bot defense stays honest).
 *
 * Also exposes:
 *   - assignVariant(): resolves the A/B variant via the backend, sticky
 *     by session id.
 *   - trackEvent(type, step_key?): fire-and-forget analytics beacon.
 *
 * Never puts PII into localStorage / cookies / query strings.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND}/api`;

// Anonymous UUID — no PII. RFC 4122 v4-ish, fine for a session tag.
function anonId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch { /* older browsers */ }
  return "sid-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function readSessionId(slug) {
  if (!slug) return null;
  try {
    const key = `lf_lp_session_${slug}`;
    let sid = window.localStorage.getItem(key);
    if (!sid) {
      sid = anonId();
      window.localStorage.setItem(key, sid);
    }
    return sid;
  } catch { return anonId(); }
}

export function useLandingSession({ slug, isPreviewSurface = false }) {
  const [sessionId] = useState(() => readSessionId(slug));
  const mountMs = useRef(Date.now());

  // Fire a single "visit" event on mount (skipped in preview surfaces).
  // Iter-52 fix: also carries the resolved variant + experiment_id when
  // available (populated by assignVariant() below) so quiz visitors are
  // not mis-bucketed as "standard" in the funnel analytics. Backend
  // additionally falls back to page.form_experience for null-variant
  // events on non-experiment pages.
  const lastAssignmentRef = useRef({ variant: null, experimentId: null });
  const visitFiredRef = useRef(false);
  useEffect(() => {
    if (isPreviewSurface || !slug || !sessionId) return;
    if (visitFiredRef.current) return;
    visitFiredRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        await axios.post(`${API}/public/landing/quiz-event`, {
          session_id: sessionId, slug, event_type: "visit",
          variant: lastAssignmentRef.current.variant || null,
          experiment_id: lastAssignmentRef.current.experimentId || null,
        });
      } catch { /* non-blocking */ }
      if (cancelled) return;
    })();
    return () => { cancelled = true; };
  }, [slug, sessionId, isPreviewSurface]);

  const assignVariant = useCallback(async ({ forceVariant, demoToken } = {}) => {
    if (!slug) return { variant: "standard" };
    try {
      const { data } = await axios.post(
        `${API}/public/landing/experiment-assign`,
        {
          session_id: sessionId, slug,
          force_variant: forceVariant || null,
          demo: demoToken || null,
        },
      );
      // Iter-52 fix: capture the resolved variant so any subsequent
      // events fire with correct attribution. If the visit event
      // already fired without a variant, back-fill it once now.
      lastAssignmentRef.current = {
        variant: data?.variant || null,
        experimentId: data?.experiment_id || null,
      };
      if (visitFiredRef.current && data?.variant && !isPreviewSurface) {
        // Fire a variant-attributed "visit" so the backend can attribute
        // this session on the next scan (idempotent-per-session upstream).
        axios.post(`${API}/public/landing/quiz-event`, {
          session_id: sessionId, slug, event_type: "visit",
          variant: data.variant,
          experiment_id: data.experiment_id || null,
        }).catch(() => {});
      }
      return data;
    } catch {
      return { variant: "standard" };
    }
  }, [slug, sessionId, isPreviewSurface]);

  const trackEvent = useCallback((event_type, opts = {}) => {
    if (isPreviewSurface || !sessionId) return;
    // Fire-and-forget; ignore all errors.
    axios.post(`${API}/public/landing/quiz-event`, {
      session_id: sessionId, slug,
      variant: opts.variant || null,
      experiment_id: opts.experimentId || null,
      event_type,
      step_key: opts.stepKey || null,
    }).catch(() => {});
  }, [sessionId, slug, isPreviewSurface]);

  return useMemo(() => ({
    sessionId,
    mountMs: mountMs.current,
    elapsedMs: () => Date.now() - mountMs.current,
    assignVariant,
    trackEvent,
  }), [sessionId, assignVariant, trackEvent]);
}

export default useLandingSession;
